import {Button} from '@nlabs/gothamjs/components';
import {CheckCircle, Clock, Download, Film, RotateCcw, TriangleAlert} from '@nlabs/gothamjs/icons';
import {formatCredits} from '@reactorbox/shared';

import {secondaryButton} from '../../theme/classes.js';
import {PreviewFrame} from '../PreviewFrame/PreviewFrame.js';

import type {OneOffVideoRequest} from '@reactorbox/shared';
import type {ReactElement} from 'react';
import type {OneOffVideoResult} from '../../state/workspaceSelectors.js';

interface VideoResultCardProps {
  readonly index: number;
  readonly onReuse: (settings: Omit<OneOffVideoRequest, 'idempotencyKey'>) => void;
  readonly result: OneOffVideoResult;
}

export const VideoResultCard = ({index, onReuse, result}: VideoResultCardProps): ReactElement => {
  const {asset, job, project, shot} = result;
  const succeeded = job.status === 'succeeded';
  const failed = job.status === 'failed';
  const reconciliation = job.status === 'reconciliation_required';
  const pending = !succeeded && !failed && !reconciliation;
  let status = 'Preview complete';
  let description = 'This job simulates generation. No playable video was created.';
  if(succeeded && !asset?.isDemo) {
    status = 'Video ready';
    description = 'Your video is ready to review and download.';
  } else if(failed) {
    status = 'Generation failed';
    description = job.error || 'This generation did not complete. Check your credit activity for the released reservation.';
  } else if(reconciliation) {
    status = 'Outcome under review';
    description = 'The provider outcome is uncertain. Credits stay reserved while this job is reconciled.';
  } else if(pending) {
    status = job.status === 'queued' ? 'Queued' : 'Generating';
    description = 'You can leave this page. Your video job will keep running.';
  }
  let creditStatus = 'reserved';
  if(succeeded) {
    creditStatus = 'spent';
  } else if(failed) {
    creditStatus = 'released';
  }
  const reuse = (): void => onReuse({
    duration: shot.duration,
    prompt: shot.description,
    referenceUrl: shot.referenceUrl,
    resolution: shot.resolution,
    title: project.name
  });

  return (
    <article className="flex min-w-0 flex-col overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.035]">
      <div className="relative aspect-[16/10] overflow-hidden bg-[#17161c]">
        {asset?.mediaUrl && !asset.isDemo ? (
          <video aria-label={`${project.name} video`} className="h-full w-full object-contain" controls poster={asset.posterUrl} preload="metadata" src={asset.mediaUrl} />
        ) : (
          <div className="absolute inset-0 [&>div]:h-full [&>div]:w-full">
            <PreviewFrame index={index} label={`${project.name} illustrative placeholder, not generated footage`} />
          </div>
        )}
        <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/45 px-3 py-2 text-[10px] font-medium backdrop-blur-xl ${failed || reconciliation ? 'text-amber-200' : 'text-white/90'}`}>
          {pending && <Clock size={12} />}
          {succeeded && <CheckCircle size={12} />}
          {(failed || reconciliation) && <TriangleAlert size={12} />}
          {status}
        </span>
        {(!asset?.mediaUrl || asset.isDemo) && <span className="absolute right-4 bottom-4 rounded-full bg-black/35 px-3 py-1.5 text-[9px] text-white/70 backdrop-blur-lg">Illustrative artwork</span>}
      </div>
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className="min-w-0 text-xl font-medium tracking-tight break-words text-white">{project.name}</h3>
          <span className="shrink-0 text-[10px] text-white/50">{new Date(job.createdAt).toLocaleString(undefined, {
            day: 'numeric', hour: 'numeric', minute: '2-digit', month: 'short'
          })}</span>
        </div>
        <p className="mb-4 line-clamp-3 text-sm leading-relaxed break-words text-white/55">{shot.description}</p>
        <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] text-white/60">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1.5"><Film size={11} />{shot.duration}s · {shot.resolution}</span>
          <span className="rounded-full border border-white/10 px-2.5 py-1.5">{formatCredits(job.costCredits)} {creditStatus}</span>
        </div>
        <p className={`mb-5 text-xs leading-relaxed ${failed || reconciliation ? 'text-amber-200/80' : 'text-white/50'}`} role={pending ? 'status' : undefined}>
          {description}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
          <Button className={`${secondaryButton} min-h-9! px-3.5! py-2! text-[11px]!`} icon={<RotateCcw size={12} />} onClick={reuse} variant="outline">Use these settings</Button>
          {asset?.mediaUrl && !asset.isDemo && (
            <a className="inline-flex min-h-9 items-center gap-1.5 rounded-full px-2 text-xs text-white/70 transition hover:text-white focus-visible:outline-2 focus-visible:outline-violet-300" download href={asset.mediaUrl}><Download size={13} />Download video</a>
          )}
        </div>
      </div>
    </article>
  );
};
